import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SEO from "../components/SEO";

function FadeIn({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}>
      {children}
    </div>
  );
}

function StatCounter({ end, label, prefix = "" }: { end: number; label: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let frame: number;
    const duration = 2000;
    const start = performance.now();
    function tick(now: number) {
      const progress = Math.min((now - start) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [end]);
  return (
    <div className="text-center">
      <div className="text-3xl md:text-4xl font-bold text-white">{prefix}{count.toLocaleString()}{end > 100 ? "+" : ""}</div>
      <div className="text-sm text-blue-100 mt-1">{label}</div>
    </div>
  );
}

const FEATURES = [
  { icon: "\ud83d\uddfa\ufe0f", title: "Personalized Roadmap", desc: "Week-by-week action plan whether you're in 9th grade or applying to grad school. Always know what to do next.", color: "from-blue-500 to-cyan-500" },
  { icon: "\ud83c\udfe0", title: "College & Program Finder", desc: "Search 1,900+ US colleges and graduate programs. AI fit scoring tells you Reach, Target, or Safety.", color: "from-violet-500 to-purple-500" },
  { icon: "\u270d\ufe0f", title: "Essay & SOP Coach", desc: "Socratic coaching for personal statements, supplements, and statements of purpose. We guide \u2014 you write.", color: "from-amber-500 to-orange-500" },
  { icon: "\ud83d\udcac", title: "Ask Madrich", desc: "24/7 AI chat that knows your profile, your list, and your timeline. Any question, anytime.", color: "from-emerald-500 to-teal-500" },
  { icon: "\ud83c\udf0d", title: "Visa & Immigration Guide", desc: "F-1 visa timeline, embassy interview prep, financial documentation checklist, and location intel for every school.", color: "from-rose-500 to-pink-500" },
  { icon: "\ud83d\udc68\u200d\ud83d\udc69\u200d\ud83d\udc67", title: "Parent Portal", desc: "Parents get their own dashboard \u2014 progress, how to help this week, and family deadlines.", color: "from-sky-500 to-indigo-500" },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <SEO
        canonical="/"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "Organization", name: "Madrich", url: "https://madrich.ai", logo: "https://madrich.ai/madrich-icon-v2-512.png", description: "AI-powered college and graduate school admissions guide for students and parents — domestic and international", sameAs: [] },
          { "@context": "https://schema.org", "@type": "WebApplication", name: "Madrich", url: "https://madrich.ai", description: "AI-powered college admissions guide for students and parents", applicationCategory: "EducationalApplication", operatingSystem: "Web", offers: [{ "@type": "Offer", name: "Student Plan", price: "299", priceCurrency: "USD", description: "Personalized roadmap, college matching, essay coaching, 24/7 AI chat" }, { "@type": "Offer", name: "Family Plan", price: "499", priceCurrency: "USD", description: "2 students + parent dashboard, shared family calendar" }, { "@type": "Offer", name: "Premium Plan", price: "999", priceCurrency: "USD", description: "Deep AI essay feedback, unlimited coaching, financial aid negotiation" }] },
          { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: [{ "@type": "Question", name: "How much does Madrich cost?", acceptedAnswer: { "@type": "Answer", text: "Starting at $299/year for students (founding member price). Family plans from $499/year." } }, { "@type": "Question", name: "Does Madrich write essays?", acceptedAnswer: { "@type": "Answer", text: "No. Madrich uses Socratic coaching — it guides you with questions, never writes for you." } }, { "@type": "Question", name: "Is there a free trial?", acceptedAnswer: { "@type": "Answer", text: "Yes. 7 active days free, no credit card required." } }] },
        ]}
      />
      {/* Fixed nav */}
      <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <img src="/madrich-icon-v2.svg" alt="Madrich" className="w-8 h-8 rounded-lg" />
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Madrich</span>
          </span>
          <div className="flex items-center gap-3">
            <Link to="/parents" className="text-sm text-slate-600 hover:text-slate-900 hidden sm:block">Parents</Link>
            <Link to="/blog" className="text-sm text-slate-600 hover:text-slate-900 hidden sm:block">Blog</Link>
            <Link to="/glossary" className="text-sm text-slate-600 hover:text-slate-900 hidden sm:block">Glossary</Link>
            <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900">Sign in</Link>
            <Link to="/register" className="text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300">
              Get started free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-20 w-[600px] h-[600px] bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-violet-400/15 to-pink-400/15 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-400/5 to-violet-400/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-8 text-center relative">
          <FadeIn>
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-base font-medium px-5 py-2 rounded-full mb-8">
              <span className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
              Now accepting founding members
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-extrabold text-slate-900 leading-[1.05] mb-8 tracking-tight">
              Your AI guide through<br />
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">admissions</span>
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="text-xl sm:text-2xl md:text-3xl text-slate-600 max-w-3xl mx-auto mb-12 leading-relaxed">
              College or grad school. Domestic or international.
              Clear direction for students and parents, every step of the way.
            </p>
          </FadeIn>
          <FadeIn delay={300}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <Link to="/register" className="group bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-10 py-5 rounded-full font-semibold text-xl hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-300 hover:-translate-y-1">
                Start free trial <span className="inline-block ml-2 group-hover:translate-x-1.5 transition-transform">&rarr;</span>
              </Link>
              <a href="#pricing" className="text-slate-600 px-10 py-5 rounded-full font-medium text-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all duration-300">
                See pricing
              </a>
            </div>
            <p className="text-base text-slate-600 mt-6">7 days free &middot; No credit card required</p>
          </FadeIn>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 py-16">
        <div className="max-w-6xl mx-auto px-8 grid grid-cols-2 md:grid-cols-4 gap-10">
          <StatCounter end={1900} label="US colleges & programs" />
          <StatCounter end={9} label="Grad degree types" />
          <StatCounter end={80} label="Roadmap milestones" />
          <StatCounter end={299} prefix="$" label="Per year founding price" />
        </div>
      </section>

      {/* Problem */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-6xl mx-auto px-8 text-center">
          <FadeIn><h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 mb-8">The system is broken</h2></FadeIn>
          <FadeIn delay={100}>
            <div className="grid gap-8 md:grid-cols-3 text-left mt-12">
              {[
                { stat: "~385:1", desc: "Est. national student-to-counselor ratio (ASCA 2023-24). Some states exceed 500:1." },
                { stat: "$3,000+", desc: "What private counselors typically charge per year. Most families can't afford it." },
                { stat: "~73%", desc: "Est. of students reporting high stress about applications (survey data)." },
              ].map((s) => (
                <div key={s.stat} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                  <div className="text-5xl font-extrabold text-red-500 mb-3">{s.stat}</div>
                  <p className="text-base text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Everything you need, one guide</h2>
            <p className="text-lg text-slate-600">Undergrad, grad school, or international &mdash; six tools working together for your success.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <FadeIn key={f.title} delay={i * 100}>
                <div className={`group relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${f.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  <div className="text-3xl mb-3">{f.icon}</div>
                  <h3 className="font-bold text-slate-900 text-lg mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12">Why Madrich?</h2>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="py-4 px-6 text-left text-slate-600 font-medium">Others</th>
                  <th className="py-4 px-6 text-left font-medium bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Madrich</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Static college lists", "AI fit scoring across 5 dimensions for every school"],
                  ["Some tools write essays for you", "Socratic coaching \u2014 we guide, you write"],
                  ["Typically $3,000+/year for private counselors", "From $299/year \u2014 founding member price"],
                  ["Appointments during business hours", "Available 24/7 \u2014 including midnight before a deadline"],
                  ["School counselors serve ~385 students (ASCA est.)", "AI that knows your full profile and history"],
                  ["Undergrad only", "Undergrad, grad school, and international students"],
                ].map(([other, madrich], i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6 text-slate-600">{other}</td>
                    <td className="py-4 px-6 text-slate-800 font-medium">{madrich}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Transparent pricing</h2>
            <p className="text-lg text-slate-600">Founding member pricing &mdash; first 20 registrants only.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {/* Student */}
            <div className="bg-white rounded-2xl border border-slate-200 p-7 text-center">
              <h3 className="font-bold text-slate-900 text-xl mb-1">Student</h3>
              <p className="text-sm text-slate-500 mb-4">For one student</p>
              <div className="mb-5">
                <span className="text-slate-500 line-through text-sm">$999/year</span>
                <div className="text-4xl font-bold text-slate-900">$299<span className="text-base font-normal text-slate-500">/yr</span></div>
                <p className="text-xs text-green-600 font-medium mt-1">Founding member &mdash; 70% off</p>
              </div>
              <ul className="text-sm text-slate-600 space-y-2 mb-6 text-left">
                {["Personalized roadmap", "College search + fit scores", "Essay coaching", "24/7 AI chat", "Deadline tracker", "Scholarship matching"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><span className="text-green-500 font-bold">&#10003;</span> {f}</li>
                ))}
              </ul>
              <Link to="/register" className="block w-full border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-medium hover:bg-blue-50 transition-all">
                Start free trial
              </Link>
            </div>
            {/* Family */}
            <div className="relative bg-white rounded-2xl border-2 border-blue-600 p-7 text-center shadow-xl shadow-blue-500/10">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs px-4 py-1 rounded-full font-medium">Most popular</span>
              </div>
              <h3 className="font-bold text-slate-900 text-xl mb-1 mt-1">Family</h3>
              <p className="text-sm text-slate-500 mb-4">2 students + parent</p>
              <div className="mb-5">
                <span className="text-slate-500 line-through text-sm">$1,499/year</span>
                <div className="text-4xl font-bold text-slate-900">$499<span className="text-base font-normal text-slate-500">/yr</span></div>
                <p className="text-xs text-green-600 font-medium mt-1">Founding member &mdash; 67% off</p>
              </div>
              <ul className="text-sm text-slate-600 space-y-2 mb-6 text-left">
                {["Everything in Student", "Unlimited essay coaching", "Mock interviews", "ED/EA strategy", "Parent dashboard", "Family sharing"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><span className="text-green-500 font-bold">&#10003;</span> {f}</li>
                ))}
              </ul>
              <Link to="/register" className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all">
                Start free trial
              </Link>
            </div>
            {/* Premium */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-7 text-white text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-4 py-1 rounded-full font-medium">Premium</span>
              </div>
              <h3 className="font-bold text-xl mb-1 mt-1">Premium</h3>
              <p className="text-sm text-slate-400 mb-4">The complete package</p>
              <div className="mb-5">
                <span className="text-slate-500 line-through text-sm">$2,499/year</span>
                <div className="text-4xl font-bold">$999<span className="text-base font-normal text-slate-400">/yr</span></div>
                <p className="text-xs text-amber-400 font-medium mt-1">Founding member &mdash; 60% off</p>
              </div>
              <ul className="text-sm text-slate-300 space-y-2 mb-6 text-left">
                {["Everything in Family", "Application review", "Passion projects", "Unlimited everything", "Priority support", "Visa & immigration guide"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><span className="text-amber-400 font-bold">&#10003;</span> {f}</li>
                ))}
              </ul>
              <Link to="/register" className="block w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all">
                Start free trial
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Real student stories */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-8">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-center mb-16">
              Built for the students no one else serves
            </h2>
          </FadeIn>
          <div className="grid gap-8 md:grid-cols-3">
            <FadeIn delay={100}>
              <div className="bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10">
                <div className="text-3xl mb-4">&#127891;</div>
                <h3 className="text-xl font-bold text-white mb-3">First-gen students</h3>
                <p className="text-slate-300 leading-relaxed">
                  No one in the family has been through it. Madrich explains everything in plain language &mdash; FAFSA, Common App, ED vs EA &mdash; like a knowledgeable older sibling.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10">
                <div className="text-3xl mb-4">&#127758;</div>
                <h3 className="text-xl font-bold text-white mb-3">International students</h3>
                <p className="text-slate-300 leading-relaxed">
                  Navigating US admissions from abroad is overwhelming. Madrich provides F-1 visa guidance, embassy interview prep, financial documentation help, and location intel for every school.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10">
                <div className="text-3xl mb-4">&#127979;</div>
                <h3 className="text-xl font-bold text-white mb-3">Grad school applicants</h3>
                <p className="text-slate-300 leading-relaxed">
                  MS, PhD, MBA, JD, MD &mdash; nine degree types supported. SOP coaching, program matching, GRE/GMAT/LSAT/MCAT tracking, and AI guidance on funding and faculty outreach.
                </p>
              </div>
            </FadeIn>
          </div>
          <div className="grid gap-8 md:grid-cols-2 mt-8 max-w-3xl mx-auto">
            <FadeIn delay={400}>
              <div className="bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10">
                <div className="text-3xl mb-4">&#128104;&#8205;&#128105;&#8205;&#128103;</div>
                <h3 className="text-xl font-bold text-white mb-3">Immigrant families</h3>
                <p className="text-slate-300 leading-relaxed">
                  Parents want to help but the system assumes knowledge they don&apos;t have. Madrich gives parents their own dashboard: what&apos;s happening, what to do, how to support.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={500}>
              <div className="bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10">
                <div className="text-3xl mb-4">&#128552;</div>
                <h3 className="text-xl font-bold text-white mb-3">Stressed-out seniors</h3>
                <p className="text-slate-300 leading-relaxed">
                  Midnight panic about deadlines. Comparing yourself to strangers on Reddit. Madrich breaks the overwhelm into small missions and is available 24/7.
                </p>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={400}>
            <p className="text-center text-lg md:text-xl text-slate-400 mt-16 max-w-2xl mx-auto leading-relaxed">
              Good guidance shouldn&apos;t depend on what your family can afford. That&apos;s why we built Madrich.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/30 rounded-full" />
          <div className="absolute bottom-10 right-20 w-48 h-48 border border-white/20 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-24 h-24 border border-white/25 rounded-full" />
        </div>
        <div className="max-w-2xl mx-auto px-6 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Start your journey today</h2>
          <p className="text-blue-100 text-lg mb-8">7 days free. No credit card required. Cancel anytime.</p>
          <Link to="/register" className="inline-block bg-white text-blue-600 px-10 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300">
            Create free account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-600 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid gap-8 md:grid-cols-4 mb-8">
            <div>
              <span className="text-lg font-bold text-white">Madrich</span>
              <p className="text-sm mt-2">Clear direction for students and parents.</p>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/parents" className="hover:text-white transition-colors">For Parents</Link></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/glossary" className="hover:text-white transition-colors">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog/fafsa-guide-how-to-apply-financial-aid" className="hover:text-white transition-colors">FAFSA Guide</Link></li>
                <li><Link to="/blog/how-to-write-common-app-essay" className="hover:text-white transition-colors">Essay Guide</Link></li>
                <li><Link to="/blog/college-admissions-timeline-complete-guide" className="hover:text-white transition-colors">Timeline</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:support@madrich.ai" className="hover:text-white transition-colors">support@madrich.ai</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex items-center justify-between text-sm">
            <span>&copy; 2026 Udabyte LLC, d/b/a Madrich. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <span>madrich.ai</span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
